import React from 'react';
import { Check } from 'lucide-react';

import { BottomSheet, PrimaryButton } from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import {
  balanceOf, modeLabel, money, ON_ACCOUNT, PAYMENT_MODES,
  paymentNote, REFERENCE_REQUIRED_MODES,
} from '@/lib/payments';

/**
 * Taking a payment at the thumb.
 *
 * The desktop pane docks off the right edge; a handset has no right edge to
 * spare, so the same form comes up from the bottom. It leads with what is owed
 * and offers to fill that in, which is what the clerk means nine times in ten.
 */
export function PaymentSheet({ payment }) {
  const {
    customer, isOpen, orders, outstanding, advance, loading,
    form, setForm, error, submitting, close, submit,
  } = payment;

  const selected = orders.find((o) => o.id === form.order_id);
  const known = outstanding !== null && outstanding !== undefined;
  const target = selected ? balanceOf(selected) : (known ? outstanding : 0);
  const note = paymentNote(form, { outstanding });
  const referenceRequired = REFERENCE_REQUIRED_MODES.includes(form.mode);

  const orderOptions = [
    { value: ON_ACCOUNT, label: 'On account (oldest orders first)' },
    ...orders.map((o) => ({ value: o.id, label: `${o.order_id} — ${money(balanceOf(o))} due` })),
  ];

  return (
    <BottomSheet
      open={isOpen}
      onClose={close}
      title="Record payment"
      subtitle={customer?.customer_name}
      testID="payment-sheet"
    >
      <div className="space-y-3">
        {(known || loading) && (
          <div
            className="flex items-baseline justify-between rounded-[10px] border px-3 py-2"
            style={{ borderColor: Colors.border.default, backgroundColor: Colors.background.secondary }}
            data-testid="payment-outstanding"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px]" style={{ color: Colors.text.tertiary }}>
              Outstanding
            </span>
            <span
              className="text-[16px] font-bold tabular-nums"
              style={{ color: outstanding > 0 ? Colors.status.error : Colors.status.success }}
            >
              {loading ? '…' : money(outstanding)}
            </span>
          </div>
        )}
        {advance > 0 && (
          <p className="text-[12px]" style={{ color: Colors.text.secondary }} data-testid="payment-advance">
            {money(advance)} already held as advance credit.
          </p>
        )}

        <Field label="Against order">
          <SelectField
            name="order_id" label="Against order" value={form.order_id}
            options={orderOptions} onChange={(v) => setForm({ order_id: v })}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Amount *"
            action={target > 0 ? (
              <button
                type="button"
                data-testid="payment-fill-balance"
                onClick={() => setForm({ amount: String(target) })}
                className="text-[11px] font-semibold"
                style={{ color: Colors.accent.DEFAULT }}
              >
                Full {money(target)}
              </button>
            ) : null}
          >
            <input
              data-testid="payment-amount-input"
              type="number" inputMode="decimal"
              className={`${INPUT_CLASS} text-right tabular-nums`}
              style={inputStyle}
              value={form.amount}
              onChange={(e) => setForm({ amount: e.target.value })}
            />
          </Field>
          <Field label="Mode *">
            <SelectField
              name="mode" label="Payment mode" value={form.mode}
              options={PAYMENT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))}
              onChange={(v) => setForm({ mode: v })}
            />
          </Field>
        </div>

        <Field label={`Reference ${referenceRequired ? '*' : ''}`}>
          <input
            className={INPUT_CLASS} style={inputStyle}
            data-testid="payment-reference-input"
            placeholder={form.mode === 'cheque' ? 'Cheque number' : 'UTR, transaction reference…'}
            value={form.reference_number}
            onChange={(e) => setForm({ reference_number: e.target.value })}
          />
        </Field>
        <Field label="Remarks">
          <input
            className={INPUT_CLASS} style={inputStyle}
            data-testid="payment-remarks-input"
            value={form.remarks}
            onChange={(e) => setForm({ remarks: e.target.value })}
          />
        </Field>

        {error && (
          <p
            className="rounded-[10px] px-3 py-2 text-[12px] leading-snug"
            style={{ backgroundColor: '#FDECEC', color: Colors.status.error }}
            data-testid="payment-error" role="alert"
          >
            {error}
          </p>
        )}
        {!error && note && (
          <p className="text-[12px] leading-snug" style={{ color: Colors.text.tertiary }} data-testid="payment-note">
            {note}
          </p>
        )}

        <PrimaryButton onClick={submit} busy={submitting} disabled={loading} testID="record-payment-btn">
          <Check className="h-4 w-4" /> Record payment
        </PrimaryButton>
      </div>
    </BottomSheet>
  );
}

export default PaymentSheet;
